import { useState, useMemo, useEffect } from 'react'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { useFirestoreArrayContainsOrderBy } from '../../firebase/useFirestore'
import IndicatorData from '../graphData/QuestionData'
import AnalysisItemNavigator from '../analysis/AnalysisItemNavigator'
import DashboardCardOptions from '../dashboard/DashboardCardOptions'
import DashboardPersonaFilter from '../dashboard/DashboardPersonaFilter'
import DashboardFilterBar from '../dashboard/DashboardFilterBar'
import usePersonaResponseCounts from '../../hooks/usePersonaResponseCounts'
import useIndicatorCardControls from '../../hooks/useIndicatorCardControls'
import { getDefaultGraphType } from '../../helpers/visualisationDefaults'
import useChartColors from '../../hooks/useChartColors'
import filterTemplateEffectsResponses from '../../helpers/liveReportHelpers/filterTemplateEffectsResponses'
import { EmptyStateProvider, useEmptyStateContext } from '../../StateManagment/EmptyStateContext'
import periodEmptyState from '../../helpers/liveReportHelpers/periodEmptyState'

// Mirrors BaseWidget.getCssValue: turns a NumberCssControl value
// ({ value, unit }) into a CSS string. 'auto' unit drops the number.
const formatCssValue = (v) => {
    if (v && typeof v === 'object' && 'value' in v && 'unit' in v) {
        return v.unit === 'auto' ? 'auto' : `${v.value}${v.unit}`
    }
    return v
}

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Shared visualisation engine for Effect data. Used by both
// `AnalysisEffectCardBody` (dashboard) and `EffectWidget` (live-report).
//
// Two per-indicator settings sources are supported, in priority order:
//   1. `nestedSettings[indicator.ID]` — used by the live-report widget
//      (settings.byNestedId, keyed by stable indicator ID).
//   2. `dashboardItem.GraphTypeByIndicator` / `ColorOverridesByIndicator` —
//      used by the dashboard. Edits save back via DashboardCardOptions.
//
// When neither is set, falls back to `graphTypeOverride` then to the
// indicator's default per its Type.
//
// templateData: when set, indicators are filtered from the pre-fetched
// template blob instead of subscribing to Firestore.

/**
 * @param {object} props
 * @param {object} props.effect
 * @param {string} [props.graphTypeOverride]
 * @param {string[]} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides]
 * @param {React.ReactNode} [props.targetgroupFilter]
 * @param {Array<object>} [props.personas]
 * @param {boolean} [props.personasEnabled]
 * @param {string} [props.selectedPersonaId]
 * @param {(personaId: string) => void} [props.onPersonaChange]
 * @param {() => void} [props.onClearFilters]
 * @param {string[]} [props.researchIdsOverride]
 * @param {boolean} [props.filterByResearchId]
 * @param {object|null} [props.dashboardItem]
 * @param {(currentField: object, effect: object) => React.ReactNode} [props.renderItemAction]
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {Object<string, Object<string, any>>} [props.nestedSettings]
 * @param {object|null} [props.templateData]
 * @param {Date|null} [props.startDate] - Period lower bound for the live data layer
 * @param {Date|null} [props.endDate] - Period upper bound (end of day) for the live data layer
 * @param {{start: string|null, end: string|null}|null} [props.periodRange] - Resolved period as ISO dates; filters template-blob rows client-side
 * @param {boolean} [props.hideControls] - Suppress this component's own chrome (filter bar, item navigator, options footer) so a wrapping ComparisonFrame can own them once. See docs/features/compare-two-periods.md
 * @param {number} [props.visible] - Controlled item index (compare mode drives both periods with one shared navigator). Omit for internal navigation.
 * @param {React.Dispatch<React.SetStateAction<number>>} [props.onVisibleChange] - Setter for the controlled item index.
 * @param {(items: Array<object>) => void} [props.onItemsChange] - Reports the resolved item list so an external navigator knows the count.
 * @returns {JSX.Element}
 */
const EffectVisualization = ({
    effect,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    targetgroupFilter,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    onClearFilters,
    researchIdsOverride,
    filterByResearchId,
    dashboardItem = null,
    onSaveOptions,
    renderItemAction,
    displayMode = 'navigator',
    nestedSettings,
    templateData,
    onCurrentItemChange,
    graphColor,
    startDate = null,
    endDate = null,
    periodRange = null,
    hideControls = false,
    visible: controlledVisible,
    onVisibleChange,
    onItemsChange,
}) => {
    // Navigation can be controlled (compare mode uses one shared navigator for
    // both periods) or internal (normal single-chart cards).
    const [internalVisible, setInternalVisible] = useState(0)
    const visible = controlledVisible != null ? controlledVisible : internalVisible
    const setVisible = onVisibleChange || setInternalVisible
    // sankeyAvailable is published by the chart layer (MatrixData) and read by
    // the graph-type picker (DashboardCardOptions). Both render inside this
    // component, so the state lives here and merges with the parent card's
    // empty-state context (type/showCta).
    const parentEmptyState = useEmptyStateContext()
    const [sankeyAvailable, setSankeyAvailable] = useState(false)
    /* With a period active the generic "gegevens verschijnen zodra ze
       beschikbaar zijn" is a false promise — the data may already exist just
       outside the window, and the window itself is nowhere on screen.
       AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */
    const emptyStateValue = useMemo(
        () => ({
            ...parentEmptyState,
            ...(periodEmptyState(startDate, endDate) || {}),
            sankeyAvailable,
            setSankeyAvailable,
        }),
        [parentEmptyState, sankeyAvailable, startDate, endDate]
    )
    const { palette } = useChartColors({ colorPalette })

    const isTemplate = !!templateData

    // Template mode: period-filter the blob's response rows once; the filtered
    // copy flows into every indicator chart. Live mode filters via
    // startDate/endDate in the data layer.
    const periodTemplateData = useMemo(
        () => filterTemplateEffectsResponses(templateData, periodRange),
        [templateData, periodRange]
    )

    // Firestore — skipped in template mode (pass '' to short-circuit).
    const liveIndicators = useFirestoreArrayContainsOrderBy(
        'QuestionnaireFields',
        'EffectId',
        isTemplate ? '' : (effect.ID || '123'),
        'IndicatorPosition',
        'asc'
    )

    const fields = useMemo(() => {
        if (isTemplate) {
            const all = templateData?.data?.effects?.indicators || []
            // Match the live Firestore listener's ordering (`IndicatorPosition` asc).
            // Without this, the template-API order leaks through and indicator #0
            // can be a different question than the dashboard's first indicator.
            return all
                .filter(i => Array.isArray(i.EffectId) && i.EffectId.includes(effect.ID))
                .slice()
                .sort((a, b) => (a.IndicatorPosition ?? 0) - (b.IndicatorPosition ?? 0))
        }
        return Array.isArray(liveIndicators) ? liveIndicators : []
    }, [isTemplate, liveIndicators, templateData, effect.ID])

    const currentField = fields[visible]

    // Surface the active carousel item so external UI can mirror navigation.
    useEffect(() => {
        if (!onCurrentItemChange) return
        onCurrentItemChange(displayMode === 'navigator' ? (currentField || null) : null)
    }, [currentField, displayMode, onCurrentItemChange])

    // Surface the full item list so an external (shared) navigator knows the
    // count — used by the compare layout to drive both periods with one nav.
    useEffect(() => {
        if (onItemsChange) onItemsChange(fields)
    }, [fields, onItemsChange])

    const fieldIds = useMemo(() => fields.map(f => f.ID).filter(Boolean), [fields])
    const showPersonaFilter = Boolean(personasEnabled) && Array.isArray(personas) && personas.length > 0
    const personaResponseCounts = usePersonaResponseCounts({
        personas: showPersonaFilter ? personas : null,
        fieldIds: showPersonaFilter ? fieldIds : null,
    })

    const { resolvedFieldType, subItems } = useIndicatorCardControls(currentField, templateData)

    /**
     * Resolve graphType / colorOverrides for a given indicator using the
     * priority chain: widget nestedSettings → dashboardItem map → fallback.
     */
    const resolvePerIndicator = (field) => {
        if (!field) return {
            graphType: graphTypeOverride || 'bar',
            colorOverrides,
        }
        const widgetEntry = nestedSettings?.[field.ID]
        const widgetGraphType = widgetEntry?.graphType
        const widgetColor = widgetEntry?.color
        const dashboardGraphType = dashboardItem?.GraphTypeByIndicator?.[field.ID]
        const dashboardColors = dashboardItem?.ColorOverridesByIndicator?.[field.ID]
        return {
            graphType: widgetGraphType
                || dashboardGraphType
                || graphTypeOverride
                || getDefaultGraphType(field.Type),
            colorOverrides: widgetColor
                // `primary` mirrors the dashboard's single-colour convention so
                // moment visualisations (e.g. the number view) that read
                // `colorOverrides.primary` honour the per-indicator colour too.
                ? { ...(colorOverrides || {}), [field.ID]: widgetColor, primary: widgetColor }
                : (dashboardColors || colorOverrides),
            // Number-styling for the 'total-growth' (single big number) view.
            numberFontSize: formatCssValue(widgetEntry?.numberFontSize),
            numberTextAlign: widgetEntry?.numberTextAlign,
            numberFontWeight: widgetEntry?.numberFontWeight,
        }
    }

    const currentPerIndicator = resolvePerIndicator(currentField)

    const saveIndicatorOptions = async (patch) => {
        // Analyse-page (transient) path: persist to caller-owned React state
        // instead of Firestore. The synthetic dashboardItem has no docid.
        if (onSaveOptions) {
            onSaveOptions(patch, currentField?.ID)
            return
        }
        if (!dashboardItem?.docid || !currentField?.ID) return
        const update = {
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'GraphType')) {
            update[`GraphTypeByIndicator.${currentField.ID}`] = patch.GraphType
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            update[`ColorOverridesByIndicator.${currentField.ID}`] = patch.ColorOverrides
        }
        await db.collection('DashboardItems').doc(dashboardItem.docid).update(update)
    }

    const renderOneIndicator = (field) => {
        const perIndicator = resolvePerIndicator(field)
        return (
            <IndicatorData
                indicator={field}
                docid={field.docid}
                effectId={effect.ID}
                effectTitle={effect.Effect}
                effectDescription={effect.Description}
                graphType={perIndicator.graphType}
                graphColor={graphColor}
                personaId={selectedPersonaId || null}
                colorPalette={palette}
                colorOverrides={perIndicator.colorOverrides}
                numberFontSize={perIndicator.numberFontSize}
                numberTextAlign={perIndicator.numberTextAlign}
                numberFontWeight={perIndicator.numberFontWeight}
                researchIdsOverride={researchIdsOverride}
                filterByResearchId={filterByResearchId}
                startDate={startDate}
                endDate={endDate}
                templateData={periodTemplateData || null}
            />
        )
    }

    return (
        <EmptyStateProvider value={emptyStateValue}>
            {!hideControls && (targetgroupFilter || showPersonaFilter) && (
                <DashboardFilterBar
                    onClear={onClearFilters || (() => {
                        if (onPersonaChange) onPersonaChange('')
                    })}
                    activeFilterCount={(researchIdsOverride ? 1 : 0) + (selectedPersonaId ? 1 : 0)}
                >
                    {targetgroupFilter}
                    {showPersonaFilter && (
                        <DashboardPersonaFilter
                            personas={personas}
                            selectedPersonaId={selectedPersonaId || ''}
                            onChange={onPersonaChange}
                            responseCounts={personaResponseCounts}
                        />
                    )}
                </DashboardFilterBar>
            )}
            {!hideControls && displayMode === 'navigator' && fields.length > 1 && (
                <div className="analysis-card-navigator-row" data-copy-hide>
                    <AnalysisItemNavigator
                        items={fields}
                        visible={visible}
                        setVisible={setVisible}
                    />
                    {renderItemAction && currentField && (
                        <div className="analysis-card-item-pin">
                            {renderItemAction(currentField, effect)}
                        </div>
                    )}
                </div>
            )}
            <div className="analysis-card-visualisation">
                {displayMode === 'stacked' ? (
                    fields.length === 0
                        ? <p className="analysis-card-empty">Geen resultaten gevonden</p>
                        : fields.map(field => (
                            <div key={field.ID} className="analysis-card-visualisation-stacked-item">
                                {renderOneIndicator(field)}
                            </div>
                        ))
                ) : (
                    currentField
                        ? renderOneIndicator(currentField)
                        : <p className="analysis-card-empty">Geen resultaten gevonden</p>
                )}
            </div>
            {!hideControls && dashboardItem && currentField && (
                <DashboardCardOptions
                    item={dashboardItem}
                    fieldType={resolvedFieldType}
                    currentGraphType={currentPerIndicator.graphType}
                    subItems={subItems}
                    onSave={saveIndicatorOptions}
                    leadingBadge={fields.length > 1 ? `${visible + 1} / ${fields.length}` : null}
                />
            )}
        </EmptyStateProvider>
    )
}

export default EffectVisualization
